


























import fuzzysort from "fuzzysort";
import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import { ClientPanel, findClientPanel } from "@/data/models/ClientPanel";
import { getAvailablePanels } from "@/data/repository/ClientPanelRepository";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { ProjectDetails } from "@/data/models/ProjectDetails";
import {
  findBranch,
  getBranchesOfClient,
} from "@/data/repository/ClientRepository";
import { BranchFilter } from "@/data/models/BranchFilter";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { getBranchName } from "@/data/models/Branch";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class FindIresDeviceForProjectAssignmentComponent extends Vue {
  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  isLoadingPanels = false;
  showNoPanelsAvailableHint = false;

  serialNumber = "";
  errorMessages: string[] = [];

  @Ref("confirmationDialog")
  confirmationDialog!: ConfirmationDialogComponent;

  @Watch("serialNumber")
  onSerialNumberChanged() {
    this.errorMessages = [];
  }

  clientPanels: Array<ClientPanel> = [];

  resolve?: (value: ClientPanel | undefined) => void = undefined;
  reject?: (reason: string) => void = undefined;

  targetBranchFilter: null | BranchFilter = null;

  async show(projectDetails: ProjectDetails) {
    this.serialNumber = "";

    const targetBranchId = projectDetails.clientData.branch;

    if (targetBranchId) {
      const branch = findBranch(targetBranchId) ?? null;

      if (branch) {
        this.targetBranchFilter = new BranchFilter(false, branch);
      }
    }

    this.baseDialogComponent.show({
      title: this.$t("assignDevice.assignHyDryDevice").toString(),
      positiveButtonText: this.$t("common.assign").toString(),
      closeOnSave: false,
      scrollable: true,
    });

    this.isLoadingPanels = true;
    this.clientPanels = (await getAvailablePanels()).sort((c1, c2) =>
      c1.serialNumber.localeCompare(c2.serialNumber)
    );

    this.isLoadingPanels = false;

    this.showNoPanelsAvailableHint = this.clientPanels.length === 0;

    return new Promise<ClientPanel | undefined>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  findByInventoryNumberFuzzy(searchText: string) {
    const inventoryNumbers: string[] = [];
    const clientPanelsWithInventoryNumber: ClientPanel[] = [];

    this.clientPanels.forEach((clientPanel) => {
      if (clientPanel.inventoryNumber && clientPanel.inventoryNumber !== "") {
        inventoryNumbers.push(clientPanel.inventoryNumber);
        clientPanelsWithInventoryNumber.push(clientPanel);
      }
    });

    const result = fuzzysort.go(searchText, inventoryNumbers, {
      allowTypo: false,
    });

    if (result.length !== 1) {
      return undefined;
    }

    const index = inventoryNumbers.indexOf(result[0].target);

    return clientPanelsWithInventoryNumber[index];
  }

  async onSave() {
    const searchText = this.serialNumber.toLowerCase();

    const result = findClientPanel(searchText, this.clientPanels);

    if (!result) {
      this.errorMessages = [
        this.$t("assignDevice.noMatchingDeviceFound").toString(),
      ];
      return;
    }

    if (result.currentProjectId) {
      this.errorMessages = [
        this.$t("assignDevice.deviceAlreadyAssigned", [
          this.serialNumber,
          result.currentProjectName ?? "",
        ]).toString(),
      ];
      return;
    }

    if (
      this.targetBranchFilter &&
      !this.targetBranchFilter.doesMatchBranchNumber(result.branch)
    ) {
      const branch = getBranchesOfClient().find(
        (branch) => branch.branchNumber.toString() === result.branch
      );

      const deviceBranchName = getBranchName(branch);
      const targetBranch = this.targetBranchFilter.isSingleBranchFilter
        ? this.targetBranchFilter.branch
        : null;

      if (targetBranch) {
        const assignToNewBranch = await this.confirmationDialog.show({
          title: this.$t("assignDeviceToProjectBranchDialog.title").toString(),
          message: this.$t("assignDeviceToProjectBranchDialog.message", [
            result.serialNumber,
            deviceBranchName,
            getBranchName(targetBranch),
          ]).toString(),
          acceptMessage: this.$t(
            "assignDeviceToProjectBranchDialog.changeBranch"
          ).toString(),
        });

        if (assignToNewBranch) {
          this.onAssignDevice(result);

          return;
        } else {
          return;
        }
      } else {
        this.errorMessages = [
          this.$t("assignDevice.deviceBranchError", [
            deviceBranchName,
          ]).toString(),
        ];
      }

      return;
    }

    this.onAssignDevice(result);
  }

  onAssignDevice(clientPanel: ClientPanel) {
    this.resolve && this.resolve(clientPanel);
    this.baseDialogComponent.isShowing = false;
  }

  onCancel() {
    this.resolve && this.resolve(undefined);
  }
}
